import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, UtensilsCrossed, Users, DollarSign, 
  Clock, CheckCircle, ChefHat, Truck, TrendingUp, Plus, Edit, Trash2, 
  GripVertical, ArrowUp, ArrowDown, FolderOpen, Upload, Image, Star, MessageSquare, Mail, Eye, Reply, Send, Megaphone, Tag, Calendar, X, ChevronLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { apiRequest, formatPrice, getAuthToken } from '../lib/utils';
import { toast } from 'sonner';
import OrderNotification from '../components/OrderNotification';
import OverviewTab from '../components/admin/OverviewTab';
import OrdersTab from '../components/admin/OrdersTab';
import MenuTab from '../components/admin/MenuTab';
import CategoriesTab from '../components/admin/CategoriesTab';
import PromotionsTab from '../components/admin/PromotionsTab';
import MessagesTab from '../components/admin/MessagesTab';
import ReviewsTab from '../components/admin/ReviewsTab';
import CustomersTab from '../components/admin/CustomersTab';
import PromoCodesTab from '../components/admin/PromoCodesTab';
import AnnouncementsTab from '../components/admin/AnnouncementsTab';
import AddonsTab from '../components/admin/AddonsTab';

const AdminPage = () => {
  const { isAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reviews, setReviews] = useState({ reviews: [], total_reviews: 0, average_rating: 0 });
  const [contacts, setContacts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('overview');
  
  // Reply state for reviews
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  
  // Reply state for messages
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const [messageReplyText, setMessageReplyText] = useState('');
  
  // Selected date for orders filter
  const [selectedOrderDate, setSelectedOrderDate] = useState(null);

  // Menu item form state
  const [isMenuDialogOpen, setIsMenuDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [menuForm, setMenuForm] = useState({
    name: '',
    description: '',
    price: '',
    price_big: '',
    category_id: '',
    image: '',
    is_available: true,
    is_popular: false,
    spice_level: null,
    ingredients: ''
  });

  // Category form state
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    image: '',
    order: 0
  });

  // Promotion form state
  const [isPromotionDialogOpen, setIsPromotionDialogOpen] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [promotionForm, setPromotionForm] = useState({
    title: '',
    description: '',
    image: '',
    button_text: 'Order Now',
    button_link: '/menu',
    is_active: true
  });
  const promotionImageRef = useRef(null);
  const [uploadingPromotionImage, setUploadingPromotionImage] = useState(false);

  // Announcement form state
  const [isAnnouncementDialogOpen, setIsAnnouncementDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    message: '',
    type: 'info',
    is_active: true,
    order: 0,
    image: ''
  });

  // File upload refs
  const menuImageRef = useRef(null);
  const announcementImageRef = useRef(null);
  const categoryImageRef = useRef(null);
  const [uploadingMenuImage, setUploadingMenuImage] = useState(false);
  const [uploadingCategoryImage, setUploadingCategoryImage] = useState(false);

  // Addon form state
  const [isAddonDialogOpen, setIsAddonDialogOpen] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [addonForm, setAddonForm] = useState({
    name: '',
    price: '',
    category_id: '',
    is_active: true
  });

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchData();
    }
  }, [authLoading, isAdmin]);

  const fetchData = async () => {
    try {
      const [statsData, ordersData, menuData, categoriesData, reviewsData, contactsData, promotionsData, customersData, promoCodesData, announcementsData, addonsData] = await Promise.all([
        apiRequest('/admin/stats'),
        apiRequest('/admin/orders'),
        apiRequest('/menu?available_only=false'),
        apiRequest('/categories'),
        apiRequest('/admin/reviews'),
        apiRequest('/admin/contacts'),
        apiRequest('/admin/promotions'),
        apiRequest('/admin/customers'),
        apiRequest('/admin/promo-codes'),
        apiRequest('/admin/announcements'),
        apiRequest('/admin/addons')
      ]);
      setStats(statsData);
      setOrders(ordersData);
      setMenuItems(menuData);
      setCategories(categoriesData);
      setReviews(reviewsData);
      setContacts(contactsData);
      setPromotions(promotionsData);
      setCustomers(customersData);
      setPromoCodes(promoCodesData);
      setAnnouncements(announcementsData);
      setAddons(addonsData);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // Promo Code Functions
  const [isPromoCodeDialogOpen, setIsPromoCodeDialogOpen] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState(null);
  const [promoCodeForm, setPromoCodeForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '0',
    max_uses: '',
    is_active: true,
    applicable_categories: [],
    expires_at: ''
  });

  const handlePromoCodeSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...promoCodeForm,
        discount_value: parseFloat(promoCodeForm.discount_value),
        min_order_amount: parseFloat(promoCodeForm.min_order_amount) || 0,
        max_uses: promoCodeForm.max_uses ? parseInt(promoCodeForm.max_uses) : null,
        expires_at: promoCodeForm.expires_at || null
      };
      
      if (editingPromoCode) {
        await apiRequest(`/admin/promo-codes/${editingPromoCode.id}`, {
          method: 'PUT',
          body: JSON.stringify(data)
        });
        toast.success('Promo code updated');
      } else {
        await apiRequest('/admin/promo-codes', {
          method: 'POST',
          body: JSON.stringify(data)
        });
        toast.success('Promo code created');
      }
      setIsPromoCodeDialogOpen(false);
      resetPromoCodeForm();
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to save promo code');
    }
  };

  const deletePromoCode = async (codeId) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    try {
      await apiRequest(`/admin/promo-codes/${codeId}`, { method: 'DELETE' });
      toast.success('Promo code deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  const resetPromoCodeForm = () => {
    setEditingPromoCode(null);
    setPromoCodeForm({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      min_order_amount: '0',
      max_uses: '',
      is_active: true,
      applicable_categories: [],
      expires_at: ''
    });
  };

  const openEditPromoCodeDialog = (code) => {
    setEditingPromoCode(code);
    setPromoCodeForm({
      code: code.code,
      discount_type: code.discount_type,
      discount_value: code.discount_value.toString(),
      min_order_amount: code.min_order_amount?.toString() || '0',
      max_uses: code.max_uses?.toString() || '',
      is_active: code.is_active,
      applicable_categories: code.applicable_categories || [],
      expires_at: code.expires_at ? code.expires_at.split('T')[0] : ''
    });
    setIsPromoCodeDialogOpen(true);
  };

  // Announcement handlers
  const handleAnnouncementSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...announcementForm,
        order: parseInt(announcementForm.order) || 0
      };

      if (editingAnnouncement) {
        await apiRequest(`/admin/announcements/${editingAnnouncement.id}`, {
          method: 'PUT',
          body: JSON.stringify(data)
        });
        toast.success('Announcement updated');
      } else {
        await apiRequest('/admin/announcements', {
          method: 'POST',
          body: JSON.stringify(data)
        });
        toast.success('Announcement created');
      }
      setIsAnnouncementDialogOpen(false);
      resetAnnouncementForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save announcement');
    }
  };

  const deleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await apiRequest(`/admin/announcements/${announcementId}`, { method: 'DELETE' });
      toast.success('Announcement deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const resetAnnouncementForm = () => {
    setEditingAnnouncement(null);
    setAnnouncementForm({
      title: '',
      message: '',
      type: 'info',
      is_active: true,
      order: 0,
      image: ''
    });
  };

  const openEditAnnouncementDialog = (announcement) => {
    setEditingAnnouncement(announcement);
    setAnnouncementForm({
      title: announcement.title,
      message: announcement.message,
      type: announcement.type,
      is_active: announcement.is_active,
      order: announcement.order || 0,
      image: announcement.image || ''
    });
    setIsAnnouncementDialogOpen(true);
  };

  // Addon Functions
  const handleAddonSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: addonForm.name,
        price: parseFloat(addonForm.price),
        category_id: addonForm.category_id,
        is_active: addonForm.is_active
      };
      if (editingAddon) {
        await apiRequest(`/admin/addons/${editingAddon.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        toast.success('Addon updated');
      } else {
        await apiRequest('/admin/addons', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        toast.success('Addon created');
      }
      setIsAddonDialogOpen(false);
      resetAddonForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save addon');
    }
  };

  const deleteAddon = async (addonId) => {
    if (!window.confirm('Are you sure you want to delete this addon?')) return;
    try {
      await apiRequest(`/admin/addons/${addonId}`, { method: 'DELETE' });
      toast.success('Addon deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete addon');
    }
  };

  const resetAddonForm = () => {
    setEditingAddon(null);
    setAddonForm({ name: '', price: '', category_id: '', is_active: true });
  };

  const openEditAddonDialog = (addon) => {
    setEditingAddon(addon);
    setAddonForm({
      name: addon.name,
      price: addon.price.toString(),
      category_id: addon.category_id,
      is_active: addon.is_active
    });
    setIsAddonDialogOpen(true);
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await apiRequest(`/admin/orders/${orderId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ order_status: newStatus })
      });
      toast.success('Order status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await apiRequest(`/admin/orders/${orderId}`, { method: 'DELETE' });
      toast.success('Order deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await apiRequest(`/admin/reviews/${reviewId}`, { method: 'DELETE' });
      toast.success('Review deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await apiRequest(`/admin/contacts/${contactId}`, { method: 'DELETE' });
      toast.success('Message deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const replyToMessage = async (contactId) => {
    if (!messageReplyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }
    try {
      await apiRequest(`/admin/contacts/${contactId}/reply`, {
        method: 'POST',
        body: JSON.stringify({ reply: messageReplyText })
      });
      toast.success('Reply sent successfully! Email sent to customer.');
      setReplyingToMessage(null);
      setMessageReplyText('');
      fetchData();
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const deleteCustomer = async (customerId, customerName) => {
    if (!window.confirm(`Are you sure you want to delete customer "${customerName}"? This action cannot be undone.`)) return;
    try {
      await apiRequest(`/admin/customers/${customerId}`, { method: 'DELETE' });
      toast.success('Customer deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete customer');
    }
  };

  const replyToReview = async (reviewId) => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }
    try {
      await apiRequest(`/admin/reviews/${reviewId}/reply`, {
        method: 'PUT',
        body: JSON.stringify({ reply: replyText })
      });
      toast.success('Reply added successfully');
      setReplyingTo(null);
      setReplyText('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add reply');
    }
  };

  // Promotion Functions
  const handlePromotionSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPromotion) {
        await apiRequest(`/admin/promotions/${editingPromotion.id}`, {
          method: 'PUT',
          body: JSON.stringify(promotionForm)
        });
        toast.success('Promotion updated');
      } else {
        await apiRequest('/admin/promotions', {
          method: 'POST',
          body: JSON.stringify(promotionForm)
        });
        toast.success('Promotion created');
      }
      setIsPromotionDialogOpen(false);
      resetPromotionForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save promotion');
    }
  };

  const deletePromotion = async (promotionId) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) return;
    try {
      await apiRequest(`/admin/promotions/${promotionId}`, { method: 'DELETE' });
      toast.success('Promotion deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete promotion');
    }
  };

  const togglePromotionActive = async (promotion) => {
    try {
      // If activating this promotion, deactivate all others first
      if (!promotion.is_active) {
        // Deactivate all other promotions
        for (const p of promotions) {
          if (p.is_active && p.id !== promotion.id) {
            await apiRequest(`/admin/promotions/${p.id}`, {
              method: 'PUT',
              body: JSON.stringify({ ...p, is_active: false })
            });
          }
        }
      }
      
      // Toggle this promotion's active state
      await apiRequest(`/admin/promotions/${promotion.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...promotion, is_active: !promotion.is_active })
      });
      
      toast.success(promotion.is_active ? 'Promotion deactivated' : 'Promotion activated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update promotion');
    }
  };

  const resetPromotionForm = () => {
    setEditingPromotion(null);
    setPromotionForm({
      title: '',
      description: '',
      image: '',
      button_text: 'Order Now',
      button_link: '/menu',
      is_active: true
    });
  };

  const openEditPromotionDialog = (promotion) => {
    setEditingPromotion(promotion);
    setPromotionForm({
      title: promotion.title,
      description: promotion.description,
      image: promotion.image || '',
      button_text: promotion.button_text || 'Order Now',
      button_link: promotion.button_link || '/menu',
      is_active: promotion.is_active
    });
    setIsPromotionDialogOpen(true);
  };

  const handlePromotionImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingPromotionImage(true);
    try {
      const imageUrl = await uploadImage(file, 'promotion');
      setPromotionForm({...promotionForm, image: imageUrl});
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploadingPromotionImage(false);
    }
  };

  // Image Upload Function
  const uploadImage = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = getAuthToken();
    
    if (!token) {
      toast.error('Authentication required. Please log in again.');
      throw new Error('No auth token');
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || `Upload failed (${response.status})`;
        toast.error(errorMsg);
        throw new Error(errorMsg);
      }
      
      const data = await response.json();
      // Return full URL
      return data.url;
    } catch (error) {
      if (!error.message.includes('Upload failed')) {
        toast.error('Failed to upload image. Please try again.');
      }
      throw error;
    }
  };

  const handleMenuImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingMenuImage(true);
    try {
      const imageUrl = await uploadImage(file, 'menu');
      setMenuForm({...menuForm, image: imageUrl});
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploadingMenuImage(false);
    }
  };

  const handleCategoryImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingCategoryImage(true);
    try {
      const imageUrl = await uploadImage(file, 'category');
      setCategoryForm({...categoryForm, image: imageUrl});
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploadingCategoryImage(false);
    }
  };

  // Menu Item Functions
  const handleMenuSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...menuForm,
        price: parseFloat(menuForm.price),
        price_big: menuForm.price_big ? parseFloat(menuForm.price_big) : null,
        spice_level: menuForm.spice_level ? parseInt(menuForm.spice_level) : null,
        ingredients: menuForm.ingredients ? menuForm.ingredients.split(',').map(i => i.trim()).filter(Boolean) : []
      };

      if (editingItem) {
        await apiRequest(`/menu/${editingItem.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        toast.success('Menu item updated');
      } else {
        await apiRequest('/menu', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        toast.success('Menu item created');
      }
      setIsMenuDialogOpen(false);
      resetMenuForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save menu item');
    }
  };

  const deleteMenuItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await apiRequest(`/menu/${itemId}`, { method: 'DELETE' });
      toast.success('Menu item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete menu item');
    }
  };

  const resetMenuForm = () => {
    setEditingItem(null);
    setMenuForm({
      name: '',
      description: '',
      price: '',
      price_big: '',
      category_id: '',
      image: '',
      is_available: true,
      is_popular: false,
      spice_level: null,
      ingredients: ''
    });
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setMenuForm({
      name: item.name,
      description: item.description,
      price: item.price.toString(),
      price_big: item.price_big ? item.price_big.toString() : '',
      category_id: item.category_id,
      image: item.image,
      is_available: item.is_available,
      is_popular: item.is_popular,
      spice_level: item.spice_level?.toString() || null,
      ingredients: (item.ingredients || []).join(', ')
    });
    setIsMenuDialogOpen(true);
  };

  // Category Functions
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...categoryForm,
        order: parseInt(categoryForm.order) || 0
      };

      if (editingCategory) {
        await apiRequest(`/categories/${editingCategory.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        toast.success('Category updated');
      } else {
        await apiRequest('/categories', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        toast.success('Category created');
      }
      setIsCategoryDialogOpen(false);
      resetCategoryForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? All items in this category will need to be reassigned.')) return;
    try {
      await apiRequest(`/categories/${categoryId}`, { method: 'DELETE' });
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      description: '',
      image: '',
      order: categories.length + 1
    });
  };

  const openEditCategoryDialog = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      image: category.image || '',
      order: category.order || 0
    });
    setIsCategoryDialogOpen(true);
  };

  const moveCategoryUp = async (category) => {
    const currentIndex = categories.findIndex(c => c.id === category.id);
    if (currentIndex <= 0) return;
    
    const prevCategory = categories[currentIndex - 1];
    
    try {
      await Promise.all([
        apiRequest(`/categories/${category.id}`, {
          method: 'PUT',
          body: JSON.stringify({ name: category.name, description: category.description, image: category.image, order: currentIndex - 1 })
        }),
        apiRequest(`/categories/${prevCategory.id}`, {
          method: 'PUT',
          body: JSON.stringify({ name: prevCategory.name, description: prevCategory.description, image: prevCategory.image, order: currentIndex })
        })
      ]);
      toast.success('Category order updated');
      fetchData();
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error('Failed to reorder category');
    }
  };

  const moveCategoryDown = async (category) => {
    const currentIndex = categories.findIndex(c => c.id === category.id);
    if (currentIndex >= categories.length - 1) return;
    
    const nextCategory = categories[currentIndex + 1];
    
    try {
      await Promise.all([
        apiRequest(`/categories/${category.id}`, {
          method: 'PUT',
          body: JSON.stringify({ name: category.name, description: category.description, image: category.image, order: currentIndex + 1 })
        }),
        apiRequest(`/categories/${nextCategory.id}`, {
          method: 'PUT',
          body: JSON.stringify({ name: nextCategory.name, description: nextCategory.description, image: nextCategory.image, order: currentIndex })
        })
      ]);
      toast.success('Category order updated');
      fetchData();
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error('Failed to reorder category');
    }
  };

  // Menu Item Reorder Functions
  const getMenuItemsByCategory = (categoryId) => {
    return menuItems
      .filter(item => item.category_id === categoryId)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  const moveMenuItemUp = async (item) => {
    const categoryItems = getMenuItemsByCategory(item.category_id);
    const currentIndex = categoryItems.findIndex(i => i.id === item.id);
    if (currentIndex <= 0) return;
    
    const prevItem = categoryItems[currentIndex - 1];
    
    try {
      await Promise.all([
        apiRequest(`/menu/${item.id}/reorder`, {
          method: 'PUT',
          body: JSON.stringify({ item_id: item.id, new_order: currentIndex - 1 })
        }),
        apiRequest(`/menu/${prevItem.id}/reorder`, {
          method: 'PUT',
          body: JSON.stringify({ item_id: prevItem.id, new_order: currentIndex })
        })
      ]);
      toast.success('Item order updated');
      fetchData();
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error('Failed to reorder item');
    }
  };

  const moveMenuItemDown = async (item) => {
    const categoryItems = getMenuItemsByCategory(item.category_id);
    const currentIndex = categoryItems.findIndex(i => i.id === item.id);
    if (currentIndex >= categoryItems.length - 1) return;
    
    const nextItem = categoryItems[currentIndex + 1];
    
    try {
      await Promise.all([
        apiRequest(`/menu/${item.id}/reorder`, {
          method: 'PUT',
          body: JSON.stringify({ item_id: item.id, new_order: currentIndex + 1 })
        }),
        apiRequest(`/menu/${nextItem.id}/reorder`, {
          method: 'PUT',
          body: JSON.stringify({ item_id: nextItem.id, new_order: currentIndex })
        })
      ]);
      toast.success('Item order updated');
      fetchData();
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error('Failed to reorder item');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-500';
      case 'confirmed': return 'bg-green-500/20 text-green-500';
      case 'preparing': return 'bg-blue-500/20 text-blue-500';
      case 'ready': return 'bg-purple-500/20 text-purple-500';
      case 'delivered': return 'bg-green-500/20 text-green-500';
      case 'cancelled': return 'bg-red-500/20 text-red-500';
      default: return 'bg-zinc-500/20 text-zinc-500';
    }
  };

  const getContactStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500/20 text-blue-500';
      case 'read': return 'bg-yellow-500/20 text-yellow-500';
      case 'replied': return 'bg-green-500/20 text-green-500';
      default: return 'bg-zinc-500/20 text-zinc-500';
    }
  };

  const updateContactStatus = async (contactId, newStatus) => {
    try {
      await apiRequest(`/admin/contacts/${contactId}/status?status=${newStatus}`, {
        method: 'PUT'
      });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (authLoading) {

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFC107]"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/login" replace />;
  }

  const tabProps = {
    addonForm,
    addons,
    announcementForm,
    announcementImageRef,
    announcements,
    categories,
    categoryForm,
    categoryImageRef,
    contacts,
    customers,
    deleteAddon,
    deleteAnnouncement,
    deleteCategory,
    deleteContact,
    deleteCustomer,
    deleteMenuItem,
    deleteOrder,
    deletePromoCode,
    deletePromotion,
    deleteReview,
    editingAddon,
    editingAnnouncement,
    editingCategory,
    editingItem,
    editingPromoCode,
    editingPromotion,
    getContactStatusColor,
    getStatusColor,
    handleAddonSubmit,
    handleAnnouncementSubmit,
    handleCategoryImageUpload,
    handleCategorySubmit,
    handleMenuImageUpload,
    handleMenuSubmit,
    handlePromoCodeSubmit,
    handlePromotionImageUpload,
    handlePromotionSubmit,
    isAddonDialogOpen,
    isAnnouncementDialogOpen,
    isCategoryDialogOpen,
    isMenuDialogOpen,
    isPromoCodeDialogOpen,
    isPromotionDialogOpen,
    menuForm,
    menuImageRef,
    menuItems,
    messageReplyText,
    moveCategoryDown,
    moveCategoryUp,
    moveMenuItemDown,
    moveMenuItemUp,
    openEditAddonDialog,
    openEditAnnouncementDialog,
    openEditCategoryDialog,
    openEditDialog,
    openEditPromoCodeDialog,
    openEditPromotionDialog,
    orders,
    promoCodeForm,
    promoCodes,
    promotionForm,
    promotionImageRef,
    promotions,
    replyText,
    replyToMessage,
    replyToReview,
    replyingTo,
    replyingToMessage,
    resetAddonForm,
    resetAnnouncementForm,
    resetCategoryForm,
    resetMenuForm,
    resetPromoCodeForm,
    resetPromotionForm,
    reviews,
    selectedOrderDate,
    setAddonForm,
    setAnnouncementForm,
    setCategoryForm,
    setIsAddonDialogOpen,
    setIsAnnouncementDialogOpen,
    setIsCategoryDialogOpen,
    setIsMenuDialogOpen,
    setIsPromoCodeDialogOpen,
    setIsPromotionDialogOpen,
    setMenuForm,
    setMessageReplyText,
    setPromoCodeForm,
    setPromotionForm,
    setReplyText,
    setReplyingTo,
    setReplyingToMessage,
    setSelectedOrderDate,
    stats,
    togglePromotionActive,
    updateContactStatus,
    updateOrderStatus,
    uploadingCategoryImage,
    uploadingMenuImage,
    uploadingPromotionImage,
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="h-8 w-8 text-[#FFC107]" />
            <h1 className="text-4xl font-bold uppercase tracking-tight text-white font-['Barlow_Condensed']">
              Admin <span className="text-[#FFC107]">Dashboard</span>
            </h1>
          </div>
          <OrderNotification onOrderAction={fetchData} />
        </div>

        <Tabs value={selectedTab} onValueChange={setSelectedTab}>
          <div className="overflow-x-auto -mx-4 px-4 mb-8">
            <TabsList className="bg-[#141417] border border-[#27272A] w-max">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
              <TabsTrigger value="menu" data-testid="tab-menu">Menu</TabsTrigger>
              <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
              <TabsTrigger value="promotions" data-testid="tab-promotions">Promotions</TabsTrigger>
              <TabsTrigger value="messages" data-testid="tab-messages" className="relative">
                Messages
                {contacts.filter(c => c.status === 'new').length > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                    {contacts.filter(c => c.status === 'new').length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
              <TabsTrigger value="customers" data-testid="tab-customers">Customers</TabsTrigger>
              <TabsTrigger value="promo-codes" data-testid="tab-promo-codes">Promo Codes</TabsTrigger>
              <TabsTrigger value="announcements" data-testid="tab-announcements">Announcements</TabsTrigger>
              <TabsTrigger value="addons" data-testid="tab-addons">Add-ons</TabsTrigger>
            </TabsList>
          </div>
          <OverviewTab {...tabProps} />
          <OrdersTab {...tabProps} />
          <MenuTab {...tabProps} />
          <CategoriesTab {...tabProps} />
          <PromotionsTab {...tabProps} />
          <MessagesTab {...tabProps} />
          <ReviewsTab {...tabProps} />
          <CustomersTab {...tabProps} />
          <PromoCodesTab {...tabProps} />
          <AnnouncementsTab {...tabProps} />
          <AddonsTab {...tabProps} />
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
